import * as next from '../../../types/next'
import type { BlockUiSchema, Journey as JourneyV3 } from '../../../utils'
import { CONTROL_NAME } from '../../../utils'
import type { UpBlockHandler } from '../types'
import {
  findBlockIdWithinStep,
  findContextParameterIdByName,
  newId
} from '../utils'

import { upCommonSettings } from './common'

export const up: UpBlockHandler<next.EntityAttributeBlock> = (
  uischema,
  _schema,
  sourceJourney
) => {
  if (uischema.type !== CONTROL_NAME.ENTITY_ATTRIBUTE_CONTROL) {
    throw new Error('Expected a valid EntityAttribute control')
  }

  if (!sourceJourney) {
    throw new Error('Expected a valid source journey')
  }

  const mode = upMode(uischema.options)

  const common: ReturnType<typeof up> = {
    type: next.BLOCK_TYPE.ENTITY_ATTRIBUTE,
    settings: {
      ...upCommonSettings(uischema),
      mode,
      readOnly: Boolean(uischema.options?.readOnly),
      entitySlug: uischema.options?.slug,
      entityAttribute: uischema.options?.attributeId
        ? {
            id: uischema.options.attributeId,
            name: uischema.options!.attributeName,
            label: uischema.options!.attributeLabel,
            type: uischema.options!.attributeType
          }
        : undefined,
      // TODO: Transform settings from BlockUiSchema
      renderSettings: {}
    },
    references: {}
  }

  switch (mode) {
    case next.ENTITY_ATTRIBUTE_MODE.ENTITY_BASED: {
      const blockId = entityFinderBlockId(
        sourceJourney,
        uischema.options!.relatedLookupBlock
      )

      if (!blockId) return common

      return Object.assign(common, {
        references: {
          [newId<next.ReferenceId>()]: {
            referenceType: next.REFERENCED_TYPE.BLOCK,
            referenceId: blockId,
            meaning: 'source'
          }
        }
      })
    }

    case next.ENTITY_ATTRIBUTE_MODE.CONTEXT_BASED: {
      const contextId = findContextParameterIdByName(
        sourceJourney,
        uischema.options!.relatedContextEntity
      )

      if (!contextId) return common

      return Object.assign(common, {
        references: {
          [newId<next.ReferenceId>()]: {
            referenceType: next.REFERENCED_TYPE.CONTEXT_PARAMETER,
            referenceId: contextId,
            meaning: 'source'
          }
        }
      })
    }

    default:
      return common
  }
}

export const upMode = (
  options: BlockUiSchema['options']
): next.ENTITY_ATTRIBUTE_MODE => {
  const mode = options?.mode

  switch (mode) {
    case 'entity_finder':
      return next.ENTITY_ATTRIBUTE_MODE.ENTITY_BASED

    case 'logged_in_contact':
      return next.ENTITY_ATTRIBUTE_MODE.AUTH_BASED

    case 'context_data':
      return next.ENTITY_ATTRIBUTE_MODE.CONTEXT_BASED

    default: {
      if (options?.contextDataCheck)
        return next.ENTITY_ATTRIBUTE_MODE.CONTEXT_BASED

      // This is the default mode when no mode is provided or when a new block is created
      return next.ENTITY_ATTRIBUTE_MODE.ENTITY_BASED
    }
  }
}

export const entityFinderBlockId = (
  /** Source journey */
  journey: JourneyV3,
  /**
   * Entity finder block reference
   * @example "0/Entity Finder", where "0" is the Step index and "Entity Finder" is the Block name
   */
  reference: string | null | undefined
) => {
  const trimmed = reference?.trim()

  if (!trimmed) return

  const [stepIndex, blockName] = trimmed.split('/')

  const blockId = findBlockIdWithinStep(journey, Number(stepIndex), blockName)

  if (!blockId) {
    throw new Error(`Block not found: ${reference}`)
  }

  return blockId
}
